import React, { useEffect, useRef, useState } from 'react';
import { FiCheck, FiChevronDown, FiLock } from 'react-icons/fi';
import { normalizeStatus, statusTone, ticketStatuses, TicketStatusName } from './TicketDetails';
import './StatusMenu.css';

/* What each status means for the admin choosing it. */
const adminHints: Record<TicketStatusName, string> = {
  Open: 'Waiting for support. The member cannot reply yet.',
  'In Progress': 'Conversation is open. Both sides can reply.',
  Resolved: 'Final. Closes the conversation and locks the status.',
};

type Props = { value?: string | null; disabled?: boolean; onChange: (status: TicketStatusName) => void };

/* Styled replacement for the ticket status <select>. Resolving asks for confirmation because it is final. */
export function StatusMenu({ value, disabled = false, onChange }: Props) {
  const current = normalizeStatus(value);
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const close = (focusTrigger = false) => { setOpen(false); setConfirming(false); if (focusTrigger) trigger.current?.focus(); };
  useEffect(() => {
    if (!open) return;
    const outside = (event: MouseEvent) => { if (!root.current?.contains(event.target as Node)) close(); };
    document.addEventListener('mousedown', outside);
    // Focus the selected option so arrow keys start from it.
    root.current?.querySelector<HTMLButtonElement>('[aria-selected="true"], [role="option"]')?.focus();
    return () => document.removeEventListener('mousedown', outside);
  }, [open]);
  useEffect(() => { if (disabled) close(); }, [disabled]);

  if (current === 'Resolved') {
    return <button type="button" className="uq-status-field is-locked" aria-label="Ticket status" title="Resolved tickets are final" disabled>
      <span className={`uq-status-dot is-${statusTone.Resolved}`} aria-hidden="true" />
      <span className="uq-status-field-label">Resolved</span>
      <FiLock className="uq-status-field-icon" aria-hidden="true" />
    </button>;
  }

  // Status only moves forward (Open -> In Progress -> Resolved); earlier steps are shown but disabled.
  const order = Object.keys(ticketStatuses) as TicketStatusName[];
  const isPast = (status: TicketStatusName) => !!current && order.indexOf(status) < order.indexOf(current);
  const pick = (status: TicketStatusName) => {
    if (isPast(status)) return;
    if (status === current) return close(true);
    if (status === 'Resolved') return setConfirming(true);
    onChange(status); close(true);
  };
  const keys = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') { event.preventDefault(); close(true); return; }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const options = Array.from(root.current?.querySelectorAll<HTMLButtonElement>('[role="option"]:not(:disabled)') || []);
    const index = options.indexOf(document.activeElement as HTMLButtonElement);
    options[(index + (event.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length]?.focus();
  };

  return <div className="uq-status-menu" ref={root} onKeyDown={keys}>
    <button ref={trigger} type="button" className="uq-status-field" aria-label="Ticket status" aria-haspopup="listbox" aria-expanded={open}
      disabled={disabled} onClick={() => (open ? close() : setOpen(true))}>
      <span className={`uq-status-dot ${current ? `is-${statusTone[current]}` : ''}`} aria-hidden="true" />
      <span className="uq-status-field-label">{current || 'Select status'}</span>
      <FiChevronDown className={`uq-status-field-icon ${open ? 'is-open' : ''}`} aria-hidden="true" />
    </button>
    {open && <div className="uq-status-pop">
      {confirming ? <div className="uq-status-confirm" role="alertdialog" aria-label="Resolve ticket">
        <strong>Resolve this ticket?</strong>
        <p>This is final. The conversation closes for both sides and the status can’t be changed again.</p>
        <div className="uq-status-confirm-actions">
          <button type="button" className="zs-btn zs-btn-secondary zs-btn-sm" onClick={() => setConfirming(false)}>Cancel</button>
          <button type="button" className="zs-btn zs-btn-primary zs-btn-sm" onClick={() => { onChange('Resolved'); close(true); }}>Resolve ticket</button>
        </div>
      </div> : <ul role="listbox" aria-label="Ticket status options">
        {order.map(status => <li key={status}>
          <button type="button" role="option" aria-selected={status === current} className="uq-status-option" disabled={isPast(status)} onClick={() => pick(status)}>
            <span className={`uq-status-dot is-${statusTone[status]}`} aria-hidden="true" />
            <span className="uq-status-option-text"><strong>{status}</strong><small>{isPast(status) ? 'Already passed. A ticket can’t move back.' : adminHints[status]}</small></span>
            {status === current && <FiCheck className="uq-status-option-check" aria-hidden="true" />}
          </button>
        </li>)}
      </ul>}
    </div>}
  </div>;
}
